import { Trans } from '@lingui/macro'
import { Percent } from '@uniswap/sdk-core'
import QuestionHelper from 'components/QuestionHelper'
import Row, { RowBetween } from 'components/Row'
import React, { useState } from 'react'
import { useUserSlippageTolerance } from 'state/user/hooks'
import { SlippageTolerance } from 'state/user/types'
import styled from 'styled-components/macro'
import { CautionTriangle, ThemedText } from 'theme'

import { Input, InputContainer } from '../Input'

enum SlippageError {
  InvalidInput = 'InvalidInput',
}

const Option = styled(Row)<{ isActive: boolean }>`
  width: auto;
  cursor: pointer;
  padding: 6px 12px;
  text-align: center;
  gap: 4px;
  border-radius: 9px;
  background: ${({ isActive, theme }) => (isActive ? theme.backgroundInteractive : 'transparent')};
  pointer-events: ${({ isActive }) => isActive && 'none'};
`

const Switch = styled(Row)`
  width: auto;
  padding: 3px;
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 12px;
`

const PresetButton = styled.button<{ isActive: boolean }>`
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme, isActive }) => (isActive ? theme.accentAction : theme.backgroundOutline)};
  background-color: ${({ theme, isActive }) => (isActive ? theme.accentAction : 'transparent')};
  color: ${({ theme, isActive }) => (isActive ? theme.white : theme.textPrimary)};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  position: relative;

  &:hover {
    border-color: ${({ theme }) => theme.accentAction};
    background-color: ${({ theme, isActive }) => (isActive ? theme.accentAction : theme.accentActionSoft)};
  }
`

const AutoBadge = styled.div`
  position: absolute;
  top: -4px;
  right: -4px;
  background-color: ${({ theme }) => theme.accentAction};
  color: ${({ theme }) => theme.white};
  font-size: 10px;
  font-weight: 600;
  padding: 2px 4px;
  border-radius: 4px;
  line-height: 1;
`

const SlippageHeader = styled(Row)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const SlippageTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const CurrentSlippageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`

const CurrentSlippageText = styled(ThemedText.BodyPrimary)<{ isWarning: boolean; isError: boolean }>`
  font-weight: 700;
  font-size: 16px;
  color: ${({ theme, isWarning, isError }) =>
    isError ? theme.accentFailure : isWarning ? theme.accentWarning : theme.textPrimary};
`

const PresetButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`
const NUMBER_WITH_MAX_TWO_DECIMAL_PLACES = /^(?:\d*\.\d{0,2}|\d+)$/
const SLIPPAGE_PRESETS = [
  { label: '0.1%', value: new Percent(1, 1000) },
  { label: '0.5%', value: new Percent(5, 1000) },
  { label: '1%', value: new Percent(1, 100) },
  { label: '2%', value: new Percent(2, 100) },
  { label: '5%', value: new Percent(5, 100) },
]
const MINIMUM_RECOMMENDED_SLIPPAGE = new Percent(1, 1_000)
const MAXIMUM_RECOMMENDED_SLIPPAGE = new Percent(1, 100)

export default function MaxSlippageSettings({ autoSlippage }: { autoSlippage: Percent }) {
  const [userSlippageTolerance, setUserSlippageTolerance] = useUserSlippageTolerance()

  // In order to trigger `custom` mode, we need to set `userSlippageTolerance` to a value that is not `auto`.
  // To do so, we use `autoSlippage` value. However, since users are likely to change that value,
  // we render it as a placeholder instead of a value.
  const defaultSlippageInputValue =
    userSlippageTolerance !== SlippageTolerance.Auto && !userSlippageTolerance.equalTo(autoSlippage)
      ? userSlippageTolerance.toFixed(2)
      : ''

  // If user has previously entered a custom slippage, we want to show that value in the input field
  // instead of a placeholder.
  const [slippageInput, setSlippageInput] = useState(defaultSlippageInputValue)
  const [slippageError, setSlippageError] = useState<SlippageError | false>(false)

  const parseSlippageInput = (value: string) => {
    // Do not allow non-numerical characters in the input field or more than two decimals
    if (value.length > 0 && !NUMBER_WITH_MAX_TWO_DECIMAL_PLACES.test(value)) {
      return
    }

    setSlippageInput(value)
    setSlippageError(false)

    // If the input is empty, set the slippage to the default
    if (value.length === 0) {
      setUserSlippageTolerance(SlippageTolerance.Auto)
      return
    }

    if (value === '.') {
      return
    }

    // Parse user input and set the slippage if valid, error otherwise
    try {
      const parsed = Math.floor(Number.parseFloat(value) * 100)
      if (parsed > 5000) {
        setSlippageError(SlippageError.InvalidInput)
      } else {
        setUserSlippageTolerance(new Percent(parsed, 10_000))
      }
    } catch (e) {
      setSlippageError(SlippageError.InvalidInput)
    }
  }

  const tooLow =
    userSlippageTolerance !== SlippageTolerance.Auto && userSlippageTolerance.lessThan(MINIMUM_RECOMMENDED_SLIPPAGE)
  const tooHigh =
    userSlippageTolerance !== SlippageTolerance.Auto && userSlippageTolerance.greaterThan(MAXIMUM_RECOMMENDED_SLIPPAGE)

  return (
    <div>
      {/* Header with title and current slippage */}
      <SlippageHeader>
        <SlippageTitle>
          <ThemedText.BodySecondary color="textPrimary">
            <Trans>Max slippage</Trans>
          </ThemedText.BodySecondary>
          <QuestionHelper
            text={
              <Trans>Your transaction will revert if the price changes unfavorably by more than this percentage.</Trans>
            }
          />
        </SlippageTitle>
        <CurrentSlippageContainer>
          <ThemedText.Caption color="textSecondary">
            <Trans>Current slippage:</Trans>
          </ThemedText.Caption>
          <CurrentSlippageText isWarning={tooLow} isError={tooHigh}>
            {userSlippageTolerance === SlippageTolerance.Auto ? (
              <Trans>Auto</Trans>
            ) : (
              `${userSlippageTolerance.toFixed(2)}%`
            )}
          </CurrentSlippageText>
        </CurrentSlippageContainer>
      </SlippageHeader>

      <RowBetween gap="md" style={{ marginBottom: '12px' }}>
        <Switch>
          <Option
            onClick={() => {
              // Reset the input field when switching to auto
              setSlippageInput('')
              setUserSlippageTolerance(SlippageTolerance.Auto)
            }}
            isActive={userSlippageTolerance === SlippageTolerance.Auto}
          >
            <ThemedText.BodyPrimary>
              <Trans>Auto</Trans>
            </ThemedText.BodyPrimary>
          </Option>
          <Option
            onClick={() => {
              // When switching to custom slippage, use `auto` value as a default.
              setUserSlippageTolerance(autoSlippage)
            }}
            isActive={userSlippageTolerance !== SlippageTolerance.Auto}
          >
            <ThemedText.BodyPrimary>
              <Trans>Custom</Trans>
            </ThemedText.BodyPrimary>
          </Option>
        </Switch>
        <InputContainer gap="md" error={!!slippageError}>
          <Input
            data-testid="slippage-input"
            placeholder={autoSlippage.toFixed(2)}
            value={slippageInput}
            onChange={(e) => parseSlippageInput(e.target.value)}
            onBlur={() => {
              // When the input field is blurred, reset the input field to the default value
              setSlippageInput(defaultSlippageInputValue)
              setSlippageError(false)
            }}
          />
          <ThemedText.BodyPrimary>%</ThemedText.BodyPrimary>
        </InputContainer>
      </RowBetween>

      {/* Preset buttons - always visible */}
      <PresetButtonsContainer>
        {SLIPPAGE_PRESETS.map((preset) => {
          const isActive = slippageInput === preset.value.toFixed(2)
          const isAutoValue = autoSlippage.equalTo(preset.value)

          return (
            <PresetButton
              key={preset.label}
              isActive={isActive}
              onClick={() => {
                if (isAutoValue) {
                  setUserSlippageTolerance(SlippageTolerance.Auto)
                  setSlippageInput('')
                } else {
                  setUserSlippageTolerance(preset.value)
                  setSlippageInput(preset.value.toFixed(2))
                }
              }}
            >
              {preset.label}
              {isAutoValue && <AutoBadge>Auto</AutoBadge>}
            </PresetButton>
          )
        })}
      </PresetButtonsContainer>

      {tooLow || tooHigh ? (
        <RowBetween gap="md" style={{ marginTop: '12px' }}>
          <CautionTriangle />
          <ThemedText.Caption color="accentWarning">
            {tooLow ? (
              <Trans>
                <strong>Warning:</strong> Low slippage may result in a failed transaction.
              </Trans>
            ) : (
              <Trans>
                <strong>Warning:</strong> High slippage may expose you to front-running attacks.
              </Trans>
            )}
          </ThemedText.Caption>
        </RowBetween>
      ) : null}
    </div>
  )
}
